import type { PluginWebManifest } from './types/web.js';

/**
 * Collects all `componentPath` strings from a plugin web manifest.
 * Walks every manifest field that references a lazy component so
 * `createWebRegistrationFromManifest` can wire them all in one pass.
 *
 * Keep this list in lockstep with `WIRING_CHECKS` in
 * `web/src/lib/plugin-registry.ts` — anything checked there for a
 * registered component but missing here will be flagged at boot as a
 * wiring gap (e.g. the impersonation plugin's `globalUiElements`
 * before this entry was added).
 */
export function collectComponentPaths(manifest: PluginWebManifest): string[] {
  const paths: string[] = [];
  for (const r of manifest.routes ?? []) paths.push(r.componentPath);
  for (const n of manifest.navItems ?? []) if (n.badge) paths.push(n.badge);
  for (const a of manifest.agentActions ?? []) if (a.componentPath) paths.push(a.componentPath);
  for (const a of manifest.adminUserActions ?? []) paths.push(a.componentPath);
  for (const t of manifest.agentTabs ?? []) paths.push(t.componentPath);
  for (const t of manifest.pageTabs ?? []) paths.push(t.componentPath);
  for (const s of manifest.settingsSections ?? []) paths.push(s.componentPath);
  for (const s of manifest.orgSettingsSections ?? []) paths.push(s.componentPath);
  for (const s of manifest.profileSections ?? []) paths.push(s.componentPath);
  for (const s of manifest.agentDetailSections ?? []) paths.push(s.componentPath);
  for (const b of manifest.agentHeaderBadges ?? []) paths.push(b.componentPath);
  for (const c of manifest.associationChips ?? []) paths.push(c.componentPath);
  for (const w of manifest.homeWidgets ?? []) paths.push(w.componentPath);
  for (const a of manifest.attachments ?? []) {
    paths.push(a.pickerComponentPath);
    if (a.summaryComponentPath) paths.push(a.summaryComponentPath);
  }
  for (const p of manifest.searchProviders ?? []) paths.push(p.componentPath);
  for (const p of manifest.launchMentionProviders ?? []) {
    paths.push(p.componentPath);
    if (p.createComponentPath) paths.push(p.createComponentPath);
    if (p.editComponentPath) paths.push(p.editComponentPath);
  }
  for (const p of manifest.chatMentionProviders ?? []) paths.push(p.componentPath);
  for (const p of manifest.agentContextProviders ?? []) {
    paths.push(p.componentPath);
    if (p.mentionBadgeComponentPath) paths.push(p.mentionBadgeComponentPath);
  }
  for (const r of manifest.flowTriggerRenderers ?? []) paths.push(r.componentPath);
  for (const r of manifest.toolCallRenderers ?? []) paths.push(r.componentPath);
  for (const p of manifest.resourcePreviews ?? []) paths.push(p.componentPath);
  for (const s of manifest.contentScanners ?? []) paths.push(s.componentPath);
  for (const p of manifest.marketingPreviews ?? []) paths.push(p.componentPath);
  for (const e of manifest.globalUiElements ?? []) paths.push(e.componentPath);
  for (const e of manifest.navUtilityElements ?? []) paths.push(e.componentPath);
  if (manifest.adminSetupComponent) paths.push(manifest.adminSetupComponent.componentPath);
  if (manifest.orgSetupComponent) paths.push(manifest.orgSetupComponent.componentPath);
  if (manifest.homePage) paths.push(manifest.homePage.componentPath);
  return [...new Set(paths)];
}

/**
 * Collects all icon name strings referenced in a plugin web manifest.
 * Walks navItems, commands, overviewCards, agentActions, agentTabs,
 * settingsSections, agentDetailSections, and attachments.
 */
export function collectIconNames(manifest: PluginWebManifest): string[] {
  const names: string[] = [];
  for (const item of manifest.navItems ?? []) names.push(item.icon);
  for (const cmd of manifest.commands ?? []) names.push(cmd.icon);
  for (const card of manifest.overviewCards ?? []) names.push(card.icon);
  for (const action of manifest.agentActions ?? []) names.push(action.icon);
  for (const tab of manifest.agentTabs ?? []) names.push(tab.icon);
  for (const tab of manifest.pageTabs ?? []) names.push(tab.icon);
  for (const section of manifest.settingsSections ?? []) names.push(section.icon);
  for (const section of manifest.orgSettingsSections ?? []) names.push(section.icon);
  for (const section of manifest.profileSections ?? []) names.push(section.icon);
  for (const section of manifest.agentDetailSections ?? []) names.push(section.icon);
  for (const attachment of manifest.attachments ?? []) names.push(attachment.icon);
  for (const provider of manifest.searchProviders ?? []) names.push(provider.icon);
  for (const provider of manifest.launchMentionProviders ?? []) names.push(provider.icon);
  for (const provider of manifest.chatMentionProviders ?? []) names.push(provider.icon);
  for (const provider of manifest.agentContextProviders ?? []) names.push(provider.icon);
  for (const renderer of manifest.flowTriggerRenderers ?? []) names.push(renderer.icon);
  for (const renderer of manifest.toolCallRenderers ?? []) names.push(renderer.icon);
  for (const route of manifest.routes ?? []) {
    if (route.marketing) names.push(route.marketing.icon);
    if (route.docs) names.push(route.docs.icon);
  }
  for (const preview of manifest.resourcePreviews ?? []) names.push(preview.icon);
  return [...new Set(names)];
}
